import { useState } from 'react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { QuestionDraftPreview } from '../../types';

interface QuestionDraftCardProps {
  data: QuestionDraftPreview;
  onAccept?: () => void;
  onDismiss?: () => void;
  savedUrl?: string;
}

const choices = ['A', 'B', 'C', 'D'] as const;

export function QuestionDraftCard({ data, onAccept, onDismiss, savedUrl }: QuestionDraftCardProps) {
  const [status, setStatus] = useState<'draft' | 'saved' | 'dismissed'>('draft');

  const handleAccept = () => {
    setStatus('saved');
    onAccept?.();
  };

  const handleDismiss = () => {
    setStatus('dismissed');
    onDismiss?.();
  };

  if (status === 'dismissed') return null;

  const answerMap: Record<string, string> = {
    A: data.answerA,
    B: data.answerB,
    C: data.answerC,
    D: data.answerD,
  };

  return (
    <Card>
      <CardHeader className="p-4 pb-2">
        <div className="flex items-center gap-2">
          <Badge variant="warning">Draft</Badge>
          <Badge variant="outline">{data.difficulty}</Badge>
          <Badge variant="secondary">{data.subject}</Badge>
        </div>
        <CardTitle className="text-sm mt-2 line-clamp-2">{data.questionText}</CardTitle>
      </CardHeader>
      <CardContent className="p-4 pt-0 space-y-1">
        {choices.map((letter) => (
          <div
            key={letter}
            className={`text-sm rounded px-2 py-1 ${
              data.correctAnswer === letter
                ? 'bg-green-100 text-green-800 font-medium dark:bg-green-900 dark:text-green-200'
                : 'text-muted-foreground'
            }`}
          >
            {letter}. {answerMap[letter]}
          </div>
        ))}
      </CardContent>
      <CardFooter className="p-4 pt-0 gap-2">
        {status === 'draft' ? (
          <>
            <Button size="sm" onClick={handleAccept}>Accept</Button>
            <Button size="sm" variant="outline" onClick={handleDismiss}>Dismiss</Button>
          </>
        ) : (
          <div className="flex items-center gap-2 text-sm text-green-600">
            <span>Saved ✓</span>
            {savedUrl && (
              <a href={savedUrl} className="underline">View</a>
            )}
          </div>
        )}
      </CardFooter>
    </Card>
  );
}
